'use strict';

import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import {ScrollToPlugin} from 'gsap/ScrollToPlugin';
import wrapWords from '../../../src/js/shared/wrapWords';

const $ = jQuery.noConflict();
$(function() {

	const heroMarquis = {

		stickyScrollThreshold: 20,

		/** Hard unlock in case the auto-scroll tween never completes. */
		unlockFallbackMs: 4500,

		init: function() {
			this.cacheDOM();
			if (!this.section) {
				return;
			}
			this.wrapRevealWords();
			this.updateNextSectionOffset();
			this.bindScrollLink();
			window.addEventListener('resize', () => this.updateNextSectionOffset());
			if (this.prefersReducedMotion()) {
				return;
			}
			gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
			ScrollTrigger.config({ignoreMobileResize: true});
			this.entranceAnimation();
			this.tl = this.createTimeline();
			// Booting mid-page (restored scroll position, late scripts) must not
			// yank the visitor into the reveal; it re-arms back at the top.
			this._autoScrollDone = window.scrollY > this.stickyScrollThreshold;
			ScrollTrigger.addEventListener('refreshInit', () => this.tl.invalidate());
			ScrollTrigger.addEventListener('refresh', () => this.updateNextSectionOffset());
		},

		cacheDOM: function() {
			this.section = document.querySelector('.js-hero');
			if (!this.section) {
				return;
			}
			this.svg = this.section.querySelector('.js-hero-svg svg');
			this.dotReveal = this.section.querySelector('.js-blob-dot-reveal');
			this.content = this.section.querySelector('.js-hero-content');
			this.introLine = this.section.querySelector('.js-hero-headline');
			this.subtitle = this.section.querySelector('.js-hero-subtitle');
			this.altButton = this.section.querySelector('.js-hero-alt-button');
			this.scrollWrap = this.section.querySelector('.js-hero-scroll');
			this.reveal = document.querySelector('.js-hero-reveal');
			this.revealIcon = this.reveal ? this.reveal.querySelector('.js-hero-icon') : null;
			this.revealHeadline = this.reveal ? this.reveal.querySelector('.js-hero-reveal-headline') : null;
			this.revealContacts = this.reveal ? this.reveal.querySelector('.js-hero-contacts') : null;
			this.revealBody = this.reveal ? this.reveal.querySelector('.js-hero-body') : null;
			this.revealContainer = this.reveal ? this.reveal.querySelector('.js-hero-reveal-container') : null;
			this.altImages = this.section.querySelectorAll('.hero__alt-image');
			this.block = this.section.closest('.fws-block');
			this.nextSection = this.block ? this.block.nextElementSibling : this.section.nextElementSibling;
			this.scrollLink = this.section.querySelector('.js-hero-scroll-link');
		},

		bindScrollLink: function() {
			if (!this.scrollLink) {
				return;
			}
			this.scrollLink.addEventListener('click', (e) => this.handleScrollLinkClick(e));
		},

		handleScrollLinkClick: function(e) {
			if (!this.tl || !this.tl.scrollTrigger) {
				return;
			}
			e.preventDefault();
			const endY = Math.round(this.tl.scrollTrigger.end);
			if (window.scrollY >= endY) {
				return;
			}
			this._autoScrollDone = true;
			gsap.to(window, {
				scrollTo: {y: endY},
				duration: this.prefersReducedMotion() ? 0 : 2.5,
				ease: 'none',
			});
		},

		updateNextSectionOffset: function() {
			if (!this.revealContainer || !this.nextSection) {
				return;
			}
			const offset = (window.innerHeight - this.revealContainer.offsetHeight) / 2;
			this.nextSection.style.marginTop = offset > 0 ? `-${offset}px` : '';
		},

		prefersReducedMotion: function() {
			return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		},

		wrapRevealWords: function() {
			if (!this.revealBody) {
				return;
			}
			const paragraphs = this.revealBody.querySelectorAll('p');
			wrapWords.wrap(paragraphs, 'hero__reveal-word', 'js-hero-reveal-word');
			this.revealWords = this.revealBody.querySelectorAll('.js-hero-reveal-word');
		},

		entranceAnimation: function() {
			const sequence = [this.introLine, this.subtitle, this.altButton, this.scrollWrap].filter(Boolean);
			const tl = gsap.timeline();

			if (sequence.length) {
				tl.to(sequence, {
					opacity: 1,
					y: 0,
					duration: 0.45,
					ease: 'power2.out',
					stagger: 0.1,
					onComplete: () => gsap.set(sequence, {clearProps: 'willChange'}),
				});
			}

			if (this.altImages && this.altImages.length) {
				tl.to(this.altImages, {
					opacity: 1,
					duration: 0.35,
					ease: 'power2.out',
					stagger: 0.12,
				}, sequence.length ? '-=0.1' : 0);

				tl.call(() => this.startFloating());
			}
		},

		startFloating: function() {
			const floatConfigs = [
				{y: -22, x: 8, duration: 3.2, delay: 0},
				{y: -16, x: -7, duration: 3.8, delay: 0.5},
				{y: -26, x: 10, duration: 2.9, delay: 0.2},
				{y: -18, x: -9, duration: 3.5, delay: 0.8},
			];

			this.altImages.forEach(function(img, i) {
				const cfg = floatConfigs[i] || floatConfigs[0];
				gsap.to(img, {
					y: cfg.y,
					x: cfg.x,
					duration: cfg.duration,
					delay: cfg.delay,
					ease: 'sine.inOut',
					repeat: -1,
					yoyo: true,
				});
			});
		},

		dotTargetR: function() {
			const svgScale = Math.max(window.innerWidth / 1920, window.innerHeight / 1268);
			const isPortrait = window.innerWidth < window.innerHeight;
			return isPortrait
				? Math.round((window.innerHeight / 2) / (svgScale * 1.5))
				: Math.round((window.innerWidth / 1.88) / (svgScale * 1.5));
		},

		createTimeline: function() {

			const tl = gsap.timeline({
				defaults: {ease: 'power1.inOut'},
				scrollTrigger: {
					trigger: this.section,
					start: 'top top',
					end: '+=300%',
					pin: true,
					pinSpacing: true,
					scrub: 0.3,
					anticipatePin: 1,
					fastScrollEnd: false,
					onUpdate: (self) => this.handleScrollUpdate(self),
				},
			});

			tl.fromTo(this.content, {opacity: 1, y: 0}, {
				opacity: 0,
				y: -60,
				duration: 8,
			}, 0);

			tl.fromTo(this.svg, {scale: 1, y: 0}, {
				scale: 1.5,
				y: () => window.innerHeight * 0.6,
				duration: 8,
			}, 4);

			tl.fromTo(this.dotReveal, {opacity: 0}, {
				opacity: 1,
				duration: 0.5,
			}, 8);

			tl.fromTo(this.dotReveal, {attr: {r: 142}}, {
				attr: {r: () => this.dotTargetR()},
				duration: 6,
			}, 8);

			const revealItems = [this.revealIcon, this.revealHeadline, this.revealContacts].filter(Boolean);

			if (revealItems.length) {
				tl.fromTo(revealItems, {opacity: 0, y: 24}, {
					opacity: 1,
					y: 0,
					duration: 2,
				}, 12);
			}

			if (this.revealBody) {
				tl.fromTo(this.revealBody, {opacity: 0}, {
					opacity: 1,
					duration: 3,
				}, 12);
			}

			if (this.revealWords && this.revealWords.length) {
				tl.fromTo(this.revealWords, {opacity: 0.2}, {
					opacity: 1,
					ease: 'none',
					stagger: {each: 0.04, from: 'start'},
					duration: 0.2,
				}, 14);
			}

			return tl;
		},

		/**
		 * Anchor links (site/scrollTo.js) scroll via jQuery animate on html/body.
		 * The auto-scroll must stay out of their way, otherwise both write the
		 * scroll position and the page lands at the end of the hero instead.
		 */
		isAnchorScrolling: function() {
			return $('html, body').is(':animated');
		},

		handleScrollUpdate: function(st) {
			const endY = Math.round(st.end);
			if (this.isAnchorScrolling()) {
				this.releaseScroll();
				this._autoScrollDone = true;
				return;
			}
			if (window.scrollY >= endY) {
				this._autoScrollDone = true;
				return;
			}
			if (window.scrollY <= this.stickyScrollThreshold) {
				this._autoScrollDone = false;
				return;
			}
			if (this._autoScrollDone || st.direction < 1) {
				return;
			}
			this.animateScrollTo(endY);
		},

		animateScrollTo: function(endY) {
			// Never stack two locks: the second would orphan the first one's
			// listeners and leave the wheel blocked for good.
			this.releaseScroll();
			this._autoScrollDone = true;
			const obj = {y: window.scrollY};
			this.lockScroll();
			this._autoTween = gsap.to(obj, {
				y: endY,
				duration: 3,
				ease: 'power1.inOut',
				onUpdate: () => window.scrollTo(0, Math.round(obj.y)),
				onComplete: () => this.releaseScroll(),
				onInterrupt: () => this.releaseScroll(),
			});
		},

		/**
		 * Single exit point for the auto-scroll: kills the tween (if it is still
		 * running) and always unlocks, so the visitor can never end up with a
		 * frozen page when the tween is interrupted by a refresh or an error.
		 */
		releaseScroll: function() {
			const tween = this._autoTween;
			this._autoTween = null;
			if (tween) {
				tween.kill();
			}
			this.unlockScroll();
		},

		/**
		 * Swallows scroll input for the duration of the animation. Anything that
		 * slips through is overridden by the tween on the next frame anyway,
		 * since it writes the scroll position on every update.
		 */
		lockScroll: function() {
			this._inputBlocker = (e) => e.preventDefault();

			window.addEventListener('wheel', this._inputBlocker, {passive: false});
			window.addEventListener('touchmove', this._inputBlocker, {passive: false});
			this._unlockTimer = window.setTimeout(() => this.releaseScroll(), this.unlockFallbackMs);
		},

		unlockScroll: function() {
			if (this._unlockTimer) {
				window.clearTimeout(this._unlockTimer);
				this._unlockTimer = null;
			}
			if (!this._inputBlocker) {
				return;
			}
			window.removeEventListener('wheel', this._inputBlocker);
			window.removeEventListener('touchmove', this._inputBlocker);
			this._inputBlocker = null;
		},

	};

	heroMarquis.init();
});
